// One cache for what an operation row loads when it is opened: N4 (the expansion), the theoretical
// (Operations) and the quality (Progress). Operations and Progress share it, and the container owns it,
// so an operation opened in one block is already loaded in the other and the data survives a block
// switch. `reset()` drops everything (order change, invalidation of Operations or Progress): answers
// still in flight are ignored and the blocks ask again for the rows that are open.
//
// A failed read stays as 'error' (the block says so and offers Retry). It is read again only on
// purpose: `retry`, or opening the row again.
import { useCallback, useRef, useState } from 'react'
import { fetchOperationExpansion, fetchOperationQuality, fetchOperationTheoretical } from './detailApi'

const KINDS = {
  expansions: { read: fetchOperationExpansion, empty: {} },
  theoreticals: { read: fetchOperationTheoretical, empty: {} },
  qualities: { read: fetchOperationQuality, empty: { inspectionReports: [] } }
}

const EMPTY = { expansions: {}, theoreticals: {}, qualities: {} }

const useOperationExpansions = (fabricationOrderId) => {
  const [slots, setSlots] = useState(EMPTY)
  // Bumped by `reset`: the blocks depend on it to ask again for the rows they show open.
  const [version, setVersion] = useState(0)
  const orderRef = useRef(fabricationOrderId)
  orderRef.current = fabricationOrderId
  // Status of every slot, written at once: two calls in the same tick must not send two requests.
  const statusRef = useRef({})
  const epochRef = useRef(0)

  const write = useCallback((kind, jobReferenceId, slot) =>
    setSlots((previous) => ({ ...previous, [kind]: { ...previous[kind], [jobReferenceId]: slot } })), [])

  const load = useCallback((kind, jobReferenceId) => {
    const epoch = epochRef.current
    const { read, empty } = KINDS[kind]
    const key = `${kind}:${jobReferenceId}`
    statusRef.current[key] = 'loading'
    write(kind, jobReferenceId, { status: 'loading', data: empty })
    read(orderRef.current, jobReferenceId)
      .then((data) => {
        if (epoch !== epochRef.current) return
        statusRef.current[key] = 'ready'
        write(kind, jobReferenceId, { status: 'ready', data })
      })
      .catch(() => {
        if (epoch !== epochRef.current) return
        statusRef.current[key] = 'error'
        write(kind, jobReferenceId, { status: 'error', data: empty })
      })
  }, [write])

  // Reads the slot only when nothing was asked for it yet.
  const ensure = useCallback((kind, jobReferenceId) => {
    if (!statusRef.current[`${kind}:${jobReferenceId}`]) load(kind, jobReferenceId)
  }, [load])

  // Reads it again only when the last read failed.
  const retry = useCallback((kind, jobReferenceId) => {
    if (statusRef.current[`${kind}:${jobReferenceId}`] === 'error') load(kind, jobReferenceId)
  }, [load])

  const reset = useCallback(() => {
    epochRef.current += 1
    statusRef.current = {}
    setSlots(EMPTY)
    setVersion((previous) => previous + 1)
  }, [])

  return { ...slots, version, ensure, retry, reset }
}

export default useOperationExpansions
